import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { GradientButton } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { COACHING_REQUEST } from '../../lib/mockCoaching';
import { submitForm } from '../../lib/api';
import { Check, CheckCircle2, ArrowRight } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy placeholder:text-ink-500/60 focus:outline-none focus:ring-2 focus:ring-purple/40';
const labelCls = 'text-[13px] font-semibold text-ink-700';

export default function CoachingRequestAdoption() {
  const d = COACHING_REQUEST;
  const [form, setForm] = useState({
    institute: '', website: '', contact: '', role: '', email: '', phone: '', city: '', learners: '',
    subjects: [], setup: '', intended: [], notes: '',
    readiness: [false, false, false],
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, val) => setForm(p => ({ ...p, [k]: p[k].includes(val) ? p[k].filter(x => x !== val) : [...p[k], val] }));
  const toggleReady = (i) => setForm(p => ({ ...p, readiness: p.readiness.map((c, ci) => ci === i ? !c : c) }));
  const allReady = form.readiness.every(Boolean);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allReady || submitting) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await submitForm({
        formType: 'coaching-start-deployment',
        fields: form,
        contactEmail: form.email,
        contactName: form.contact,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <section className="px-6 py-24" data-testid="coaching-request-confirmation">
          <div className="mx-auto max-w-[720px]">
            <div className="rounded-3xl border border-mint/30 bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-10 shadow-soft text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-mint text-white shadow"><CheckCircle2 className="h-8 w-8" /></div>
              <h2 className="mt-5 text-[26px] md:text-[30px] font-extrabold text-navy">{d.confirmation.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-700">{d.confirmation.body}</p>
              <div className="mt-6"><GradientButton to={d.confirmation.cta.to}>{d.confirmation.cta.label}</GradientButton></div>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="coaching-request-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{d.hero.eyebrow}</div>
          <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[36px] md:text-[46px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-canvas-100 p-5 shadow-soft">
              <div className="text-[13px] font-bold text-navy">{d.hero.readinessTitle}</div>
              <ul className="mt-3 space-y-2">
                {d.hero.readiness.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-purple flex-none" /> {r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="px-6 py-8">
        <div className="mx-auto max-w-[1240px] grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr]">
          <form onSubmit={onSubmit} className="rounded-3xl border border-canvas-200 bg-white p-6 md:p-10 shadow-soft space-y-5" data-testid="coaching-request-form">
            <div>
              <h2 className="text-[20px] font-extrabold text-navy">{d.formTitle}</h2>
              <p className="mt-1 text-[12.5px] text-ink-500">{d.formHint}</p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Institute name *</label>
                <input required data-testid="ca-institute" value={form.institute} onChange={e => set('institute', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter coaching institute name" />
              </div>
              <div>
                <label className={labelCls}>Website</label>
                <input type="url" data-testid="ca-website" value={form.website} onChange={e => set('website', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Optional" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Contact person *</label>
                <input required data-testid="ca-contact" value={form.contact} onChange={e => set('contact', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter full name" />
              </div>
              <div>
                <label className={labelCls}>Role *</label>
                <input required data-testid="ca-role" value={form.role} onChange={e => set('role', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Director, coordinator, faculty or administrator" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" required data-testid="ca-email" value={form.email} onChange={e => set('email', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="name@institute.org" />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input required data-testid="ca-phone" value={form.phone} onChange={e => set('phone', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter phone number" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>City *</label>
                <input required data-testid="ca-city" value={form.city} onChange={e => set('city', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter city" />
              </div>
              <div>
                <label className={labelCls}>Approximate number of Grade 8–9 learners *</label>
                <input required data-testid="ca-learners" type="number" min="1" value={form.learners} onChange={e => set('learners', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter learner count" />
              </div>
            </div>
            <div>
              <div className={labelCls}>Subjects currently offered *</div>
              <div className="mt-3 flex flex-wrap gap-3">
                {d.subjects.map(s => {
                  const on = form.subjects.includes(s);
                  return (
                    <label key={s} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-[13.5px] font-semibold shadow-soft transition-colors ${on ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`ca-subject-${s.toLowerCase()}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleArr('subjects', s)} className="sr-only" /> {s}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <div className={labelCls}>Preferred starting setup *</div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {d.setupOptions.map(s => (
                  <label key={s} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-center text-[13px] font-semibold shadow-soft transition-colors ${form.setup === s ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`ca-setup-${s.replace(/\s+/g,'-').toLowerCase()}`}>
                    <input type="radio" name="setup" value={s} checked={form.setup === s} onChange={e => set('setup', e.target.value)} className="sr-only" /> {s}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className={labelCls}>Intended use</div>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {d.intendedUse.map(u => {
                  const on = form.intended.includes(u);
                  return (
                    <label key={u} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-[13px] font-semibold shadow-soft transition-colors ${on ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleArr('intended', u)} className="sr-only" /> {u}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className={labelCls}>Questions or requirements</label>
              <textarea rows={3} data-testid="ca-notes" value={form.notes} onChange={e => set('notes', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Share timetable, hardware, analytics or support questions." />
            </div>
            <div>
              <div className={labelCls}>Readiness</div>
              <div className="mt-3 space-y-2">
                {d.readiness.map((r, i) => (
                  <label key={i} className={`flex items-start gap-3 rounded-xl border p-4 shadow-soft cursor-pointer transition-colors ${form.readiness[i] ? 'border-mint bg-mint-50' : 'border-canvas-200 bg-white hover:border-purple/40'}`} data-testid={`ca-ready-${i}`}>
                    <input type="checkbox" checked={form.readiness[i]} onChange={() => toggleReady(i)} className="mt-1 h-4 w-4 rounded border-canvas-200 text-purple focus:ring-purple/40" />
                    <span className="text-[13.5px] text-ink-700">{r}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-canvas-200">
              <button type="submit" disabled={!allReady || submitting} data-testid="ca-submit" className={`inline-flex items-center gap-1 rounded-lg px-6 py-3 text-[14px] font-semibold text-white shadow-soft ${allReady && !submitting ? 'bg-brand-gradient hover:brightness-110' : 'bg-canvas-200 text-ink-500 cursor-not-allowed'}`}>{submitting ? 'Submitting…' : d.submit} <ArrowRight className="h-4 w-4" /></button>
              <div className="text-[12px] text-ink-500">
                <div>{d.submitNote}</div>
                <div className="mt-0.5 italic">{d.privacy}</div>
              </div>
            </div>
            {submitError && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[12.5px] text-red-700" data-testid="ca-submit-error">{submitError}</div>
            )}
          </form>

          {/* Side panel */}
          <div className="space-y-6">
            <div className="rounded-3xl border border-canvas-200 bg-white p-6 shadow-soft">
              <h3 className="text-[18px] font-extrabold text-navy">{d.nextTitle}</h3>
              <ol className="mt-4 space-y-3">
                {d.next.map((n, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-purple text-white text-[13px] font-bold">{i + 1}</span>
                    <div>
                      <div className="text-[13.5px] font-bold text-navy">{n.title}</div>
                      <div className="text-[12px] text-ink-500">{n.desc}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-6 shadow-soft">
              <h3 className="text-[18px] font-extrabold text-navy">{d.beforeTitle}</h3>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {d.beforeItems.map((b, i) => (
                  <div key={i} className="rounded-xl bg-canvas-100 p-3 border border-canvas-200">
                    <div className="text-[18px] font-extrabold text-purple">{b.k}</div>
                    <div className="mt-1 text-[13px] font-bold text-navy">{b.title}</div>
                    <div className="text-[11.5px] text-ink-500">{b.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24" />
    </PageShell>
  );
}
